import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, XCircle, Edit, Eye, ExternalLink, Plus, Trash2, Undo2 } from "lucide-react";
import { UnapproveLeadDialog } from "@/components/leads/UnapproveLeadDialog";

export default function LeadApprovals({ user }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [unapproving, setUnapproving] = useState(null);
  const canUnapprove = user?.department === "Management" || user?.access_level === "Admin";
  
  // Review dialog states
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [currentApproval, setCurrentApproval] = useState(null);
  const [reviewMode, setReviewMode] = useState("view"); // "view", "edit", "approve", "reject"
  const [reviewNotes, setReviewNotes] = useState("");
  
  // Edit form data
  const [editFormData, setEditFormData] = useState({
    lead_name: "",
    lead_status: "",
    industry: "",
    phone: "",
    fax: "",
    website: "",
    time_zone: "EST",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    sales_user_id: ""
  });
  
  // Contact editing
  const [contacts, setContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editContactIndex, setEditContactIndex] = useState(null);
  const [contactFormData, setContactFormData] = useState({
    first_name: "",
    last_name: "",
    business_phone: "",
    business_extension: "",
    mobile_phone: "",
    email: "",
    designation: "",
    roles: [],
    special_notes: ""
  });
  
  // Users for sales assignment dropdown
  const [users, setUsers] = useState([]);
  
  const roleOptions = ["Project", "Billing", "Management", "FDO", "Estimator", "Owner"];

  useEffect(() => {
    fetchApprovals();
    fetchStats();
    fetchUsers();
  }, [activeTab]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/lead-approvals?status=${activeTab}`);
      setApprovals(response.data);
    } catch (error) {
      toast.error("Failed to fetch lead approvals");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get("/lead-approvals/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const openReviewDialog = (approval, mode) => {
    setCurrentApproval(approval);
    setReviewMode(mode);
    setReviewNotes("");
    
    if (mode === "edit") {
      setEditFormData({
        lead_name: approval.lead_name || "",
        lead_status: approval.lead_status || "",
        industry: approval.industry || "",
        phone: approval.phone || "",
        fax: approval.fax || "",
        website: approval.website || "",
        time_zone: approval.time_zone || "EST",
        address_line1: approval.address_line1 || "",
        address_line2: approval.address_line2 || "",
        city: approval.city || "",
        state: approval.state || "",
        zip_code: approval.zip_code || "",
        country: approval.country || "",
        sales_user_id: approval.sales_user_id || ""
      });
      setContacts(approval.contacts || []);
    }
    
    setReviewDialogOpen(true);
  };

  const handleUpdateApproval = async () => {
    try {
      const updateData = { ...editFormData, contacts };
      await api.put(`/lead-approvals/${currentApproval.id}`, updateData);
      toast.success("Approval data updated successfully");
      setReviewDialogOpen(false);
      fetchApprovals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update approval");
    }
  };

  const handleApproveReject = async (action) => {
    try {
      const endpoint = action === "approve" ? "approve" : "reject";
      await api.patch(`/lead-approvals/${currentApproval.id}/${endpoint}`, {
        approval_notes: reviewNotes
      });
      toast.success(`Lead ${action === "approve" ? "approved" : "rejected"} successfully`);
      setReviewDialogOpen(false);
      fetchApprovals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} lead`);
    }
  };

  // Contact management
  const openContactDialog = (index = null) => {
    if (index !== null) {
      const contact = contacts[index];
      setContactFormData(contact);
      setEditContactIndex(index);
    } else {
      setContactFormData({
        first_name: "",
        last_name: "",
        business_phone: "",
        business_extension: "",
        mobile_phone: "",
        email: "",
        designation: "",
        roles: [],
        special_notes: ""
      });
      setEditContactIndex(null);
    }
    setContactDialogOpen(true);
  };

  const handleSaveContact = () => {
    const newContacts = [...contacts];
    if (editContactIndex !== null) {
      newContacts[editContactIndex] = contactFormData;
    } else {
      newContacts.push({ ...contactFormData, id: `temp_${Date.now()}` });
    }
    setContacts(newContacts);
    setContactDialogOpen(false);
  };

  const handleDeleteContact = (index) => {
    const newContacts = contacts.filter((_, i) => i !== index);
    setContacts(newContacts);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "N/A";
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800",
      approved: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800"
    };
    return (
      <Badge className={variants[status] || "bg-md3-surface-container text-md3-on-surface"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <Layout user={user}>
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">Lead Approvals</h1>
            <p className="text-md3-on-surface-variant">Review and approve leads for client conversion</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending {stats.pending > 0 && `(${stats.pending})`}
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved {stats.approved > 0 && `(${stats.approved})`}
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected {stats.rejected > 0 && `(${stats.rejected})`}
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface ">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Lead Approvals</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : approvals.length === 0 ? (
                  <div className="text-center py-8 text-md3-on-surface-variant">
                    No {activeTab} approvals found
                  </div>
                ) : (
                  <Table className="bg-md3-surface-container rounded-md3-md">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lead Number</TableHead>
                        <TableHead>Lead Name</TableHead>
                        <TableHead>Industry</TableHead>
                        <TableHead>Sales User</TableHead>
                        <TableHead>Requested Date</TableHead>
                        {activeTab !== "pending" && <TableHead>Reviewed By</TableHead>}
                        {activeTab !== "pending" && <TableHead>Reviewed Date</TableHead>}
                        {activeTab === "approved" && <TableHead>Conversion Status</TableHead>}
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {approvals.map((approval) => (
                        <TableRow key={approval.id}>
                          <TableCell className="font-medium">{approval.lead_number}</TableCell>
                          <TableCell>{approval.lead_name}</TableCell>
                          <TableCell>{approval.industry || "N/A"}</TableCell>
                          <TableCell>{approval.sales_user_name || "Unassigned"}</TableCell>
                          <TableCell>{formatDate(approval.created_at)}</TableCell>
                          {activeTab !== "pending" && (
                            <TableCell>{approval.reviewed_by_name || "N/A"}</TableCell>
                          )}
                          {activeTab !== "pending" && (
                            <TableCell>{formatDate(approval.reviewed_at)}</TableCell>
                          )}
                          {activeTab === "approved" && (
                            <TableCell>
                              {approval.converted_to_client_id ? (
                                <Badge className="bg-blue-100 text-blue-800">
                                  Converted
                                </Badge>
                              ) : (
                                <Badge className="bg-md3-surface-container text-md3-on-surface">
                                  Available
                                </Badge>
                              )}
                            </TableCell>
                          )}
                          <TableCell>{getStatusBadge(approval.approval_status)}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button size="sm"
                                variant="outline"
                                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openReviewDialog(approval, "view")}>
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {activeTab === "pending" && (
                                <>
                                  <Button size="sm"
                                    variant="outline"
                                    className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openReviewDialog(approval, "edit")}>
                                    <Edit className="w-4 h-4 mr-1" />
                                    Edit
                                  </Button>
                                  <Button size="sm"
                                    variant="default"
                                    className="bg-green-600 hover:bg-green-700 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"  onClick={() => openReviewDialog(approval, "approve")}>
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve
                                  </Button>
                                  <Button size="sm"
                                    variant="destructive"
                                    className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openReviewDialog(approval, "reject")}>
                                    <XCircle className="w-4 h-4 mr-1" />
                                    Reject
                                  </Button>
                                </>
                              )}
                              {activeTab === "approved" && canUnapprove && !approval.converted_to_client_id && (
                                <Button size="sm" variant="outline" className="rounded-md3-full text-amber-800 border-amber-300 hover:bg-amber-50" onClick={() => setUnapproving(approval)} data-testid={`lead-unapprove-btn-${approval.id}`}>
                                  <Undo2 className="w-4 h-4 mr-1" />
                                  Unapprove
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Review/Edit Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                {reviewMode === "view" && "View Lead Details"}
                {reviewMode === "edit" && "Edit Lead Approval"}
                {reviewMode === "approve" && "Approve Lead"}
                {reviewMode === "reject" && "Reject Lead"}
              </DialogTitle>
              <DialogDescription className="text-md3-body-medium text-gray-600">
                {reviewMode === "view" && "Review the lead details"}
                {reviewMode === "edit" && "Edit lead information and contacts before approval"}
                {reviewMode === "approve" && "Approve this lead for client conversion"}
                {reviewMode === "reject" && "Reject this lead approval"}
              </DialogDescription>
            </DialogHeader>

            {currentApproval && (
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Lead Number (Read-only)</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={currentApproval.lead_number} disabled />
                  </div>
                  <div>
                    <Label>Lead Name</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.lead_name : currentApproval.lead_name}
                      onChange={(e) => setEditFormData({ ...editFormData, lead_name: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={reviewMode === "edit" ? editFormData.lead_status : currentApproval.lead_status}
                      onValueChange={(value) => setEditFormData({ ...editFormData, lead_status: value })}
                      disabled={reviewMode !== "edit"}
                    >
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Recording">Recording</SelectItem>
                        <SelectItem value="Estimate">Estimate</SelectItem>
                        <SelectItem value="RFQ">RFQ</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Industry</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.industry : currentApproval.industry}
                      onChange={(e) => setEditFormData({ ...editFormData, industry: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.phone : currentApproval.phone}
                      onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.website : currentApproval.website}
                      onChange={(e) => setEditFormData({ ...editFormData, website: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                </div>

                {/* Address */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Address Line 1</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.address_line1 : currentApproval.address_line1}
                      onChange={(e) => setEditFormData({ ...editFormData, address_line1: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.city : currentApproval.city}
                      onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.state : currentApproval.state}
                      onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>Zip Code</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.zip_code : currentApproval.zip_code}
                      onChange={(e) => setEditFormData({ ...editFormData, zip_code: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewMode === "edit" ? editFormData.country : currentApproval.country}
                      onChange={(e) => setEditFormData({ ...editFormData, country: e.target.value })}
                      disabled={reviewMode !== "edit"}
                    />
                  </div>
                </div>

                {/* Sales User */}
                <div>
                  <Label>Assigned Sales User</Label>
                  {reviewMode === "edit" ? (
                    <Select
                      value={editFormData.sales_user_id}
                      onValueChange={(value) => setEditFormData({ ...editFormData, sales_user_id: value })}
                    >
                      <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                        <SelectValue placeholder="Select sales user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.department === "Sales").map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={currentApproval.sales_user_name || "Unassigned"} disabled />
                  )}
                </div>

                {/* Contacts Section */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-md3-title-large ">Contacts ({reviewMode === "edit" ? contacts.length : currentApproval.contacts.length})</Label>
                    {reviewMode === "edit" && (
                      <Button size="sm" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openContactDialog()}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Contact
                      </Button>
                    )}
                  </div>
                  <div className="border rounded-md3-lg p-4 space-y-2 max-h-60 overflow-y-auto">
                    {(reviewMode === "edit" ? contacts : currentApproval.contacts).length === 0 ? (
                      <p className="text-md3-on-surface-variant text-md3-body-small">No contacts added</p>
                    ) : (
                      (reviewMode === "edit" ? contacts : currentApproval.contacts).map((contact, index) => (
                        <div key={index} className="flex justify-between items-start p-3 bg-md3-surface rounded">
                          <div>
                            <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">{contact.designation || "N/A"}</p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">{contact.email || "No email"}</p>
                            <p className="text-md3-body-small text-md3-on-surface-variant">
                              {contact.roles && contact.roles.length > 0 ? contact.roles.join(", ") : "No roles"}
                            </p>
                          </div>
                          {reviewMode === "edit" && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => openContactDialog(index)}>
                                <Edit className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="destructive" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={() => handleDeleteContact(index)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Review Notes (for approve/reject) */}
                {(reviewMode === "approve" || reviewMode === "reject") && (
                  <div>
                    <Label>Review Notes {reviewMode === "reject" && "(Required)"}</Label>
                    <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Enter notes about your decision..."
                      rows={4}
                    />
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setReviewDialogOpen(false)}>
                    Cancel
                  </Button>
                  {reviewMode === "edit" && (
                    <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={handleUpdateApproval}>
                      Save Changes
                    </Button>
                  )}
                  {reviewMode === "approve" && (
                    <Button className="bg-green-600 hover:bg-green-700 bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"  onClick={() => handleApproveReject("approve")}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve Lead
                    </Button>
                  )}
                  {reviewMode === "reject" && (
                    <Button
                      variant="destructive"
                      onClick={() => handleApproveReject("reject")}
                      disabled={!reviewNotes.trim()}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject Lead
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Contact Edit Dialog */}
        <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editContactIndex !== null ? "Edit Contact" : "Add Contact"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.first_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.last_name}
                    onChange={(e) => setContactFormData({ ...contactFormData, last_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Business Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.business_phone}
                    onChange={(e) => setContactFormData({ ...contactFormData, business_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Extension</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.business_extension}
                    onChange={(e) => setContactFormData({ ...contactFormData, business_extension: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Mobile Phone</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.mobile_phone}
                    onChange={(e) => setContactFormData({ ...contactFormData, mobile_phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.email}
                    onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Designation</Label>
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.designation}
                    onChange={(e) => setContactFormData({ ...contactFormData, designation: e.target.value })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Roles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {roleOptions.map((role) => (
                    <label key={role} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={contactFormData.roles.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setContactFormData({
                              ...contactFormData,
                              roles: [...contactFormData.roles, role]
                            });
                          } else {
                            setContactFormData({
                              ...contactFormData,
                              roles: contactFormData.roles.filter((r) => r !== role)
                            });
                          }
                        }}
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label>Special Notes</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={contactFormData.special_notes}
                  onChange={(e) => setContactFormData({ ...contactFormData, special_notes: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2"  onClick={() => setContactDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveContact}
                  disabled={!contactFormData.first_name || !contactFormData.last_name}
                >
                  {editContactIndex !== null ? "Update" : "Add"} Contact
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <UnapproveLeadDialog approval={unapproving} onClose={() => setUnapproving(null)} onDone={() => { fetchApprovals(); fetchStats(); }} />
      </div>
    </Layout>
  );
}
